import React, { useState, useEffect, useContext } from 'react';
import axios from '../helpers/axiosConfig';
import { toast } from 'react-toastify';
import { AuthContext } from '../helpers/AuthContext';

const SearchForm = ({ setPersonnels, setStats }) => {
  const { authState } = useContext(AuthContext);
  const user = authState.user;

  const [formFields, setFormFields] = useState({
    nomprenom: '',
    gradeId: '',
    etsouserviceId: '',
    brigadeId: [],
    bataillonId: [],
    compagnieId: [],
    operation: '',
    boadssId: '',
    exportExcel: false,
    fonction: '',
    pdl: false,
    annee: '',
    vague: '',
    observation: '',
    nonVerifier: false,
    diplomeadministratif: ''
  });

  const [etsouservice, setEtsouservices] = useState([]);
  const [grades, setGrades] = useState([]);
  const [brigade, setBrigade] = useState([]);
  const [bataillon, setBataillon] = useState([]);
  const [compagnie, setCompagnie] = useState([]);
  const [boaDss, setBoaDss] = useState([]);

  useEffect(() => {
    const fetchInitialData = async () => {
      const [servivesRes, gradeRes, brigadeRes] = await Promise.all([
        axios.get('/etsouservice/'),
        axios.get('/grade/'),
        axios.get('/brigade/')
      ]);
      setBrigade(brigadeRes.data);
      setGrades(gradeRes.data);
      setEtsouservices(servivesRes.data);
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (user?.roles === "USER" && brigade.length > 0) {
      const username = user.username.toUpperCase().replace(/^BRCA/, "").trim();
      const foundBrigade = brigade.find(b => b.name.toUpperCase().replace(/^BRCA/, "").trim() === username);
      if (foundBrigade) setFormFields(prev => ({ ...prev, brigadeId: foundBrigade.id }));
    }
  }, [user, brigade]);

  useEffect(() => {
    if (formFields.brigadeId) {
      axios.get(`/bataillon/${formFields.brigadeId}`).then(res => setBataillon(res.data));
    } else {
      setBataillon([]);
      setFormFields(prev => ({ ...prev, bataillonId: '', compagnieId: '' }));
    }
  }, [formFields.brigadeId]);

  useEffect(() => {
    if (user?.roles === "USER" && bataillon.length > 0) {
      setFormFields(prev => ({ ...prev, bataillonId: bataillon[0].id }));
    }
  }, [bataillon, user]);

  useEffect(() => {
    if (formFields.bataillonId) {
      axios.get(`compagnie/${formFields.bataillonId}`).then(res => setCompagnie(res.data.compagnie));
    } else {
      setCompagnie([]);
      setFormFields(prev => ({ ...prev, compagnieId: '' }));
    }
    axios.get('/boadss').then(res => setBoaDss(res.data));
  }, [formFields.bataillonId]);

  const handleInputChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormFields(prev => {
      if (name === "exportExcel" && type === "checkbox")
        return { ...prev, exportExcel: checked, nonVerifier: checked ? false : prev.nonVerifier };
      if (name === "nonVerifier" && type === "checkbox")
        return { ...prev, nonVerifier: checked, exportExcel: checked ? false : prev.exportExcel };
      return { ...prev, [name]: type === "checkbox" ? checked : value };
    });
  };

  const supprimerPDL = async () => {
    if (!formFields.pdl) return toast.error("Veuillez cocher la case PDL !");
    if (!formFields.annee.trim()) return toast.error("Veuillez saisir l'année !");
    if (!formFields.vague.trim()) return toast.error("Veuillez saisir la vague !");
    try {
      const token = localStorage.getItem("accessToken");
      const res = await axios.delete("/personnel/delete-pdl/v/25", {
        headers: { Authorization: `Bearer ${token}` },
        data: { annee: formFields.annee, vague: formFields.vague }
      });
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.error || "Erreur lors de la suppression");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("accessToken");
    try {
      const config = { params: formFields, headers: { Authorization: `Bearer ${token}` } };
      if (formFields.exportExcel) config.responseType = "blob";
      const res = await axios.get("/personnel", config);
      if (formFields.exportExcel) {
        const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Liste_Personnels_${Date.now()}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success("Fichier Excel téléchargé !");
        return;
      }
      setPersonnels(res.data.personnels);
      setStats(res.data.stats);
    } catch (err) {
      toast.error(err.response?.data?.error || "Erreur");
    }
  };

  const handleExportExcelChange = (checked) => {
    setFormFields(prev => ({ ...prev, exportExcel: checked, nonVerifier: checked ? prev.nonVerifier : false }));
  };

  const handleNonVerifierChange = (checked) => {
    setFormFields(prev => ({ ...prev, exportExcel: checked ? true : prev.exportExcel, nonVerifier: checked }));
  };

  // Classes réutilisables
  const inputCls = "w-full bg-slate-800 border border-cyan-500/30 text-white text-sm rounded-xl px-3 py-2 focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all placeholder-slate-500";
  const labelCls = "block text-xs font-medium text-cyan-400 mb-1";

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 mb-6">

      {/* Titre */}
      <div className="flex items-center gap-2 mb-5">
        <i className="fa fa-search text-green-400"></i>
        <h2 className="text-lg font-semibold text-white">Recherche</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">

        {/* Ligne 1 : Brigade / Bataillon / Compagnie */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Brigade</label>
            <select value={formFields.brigadeId} onChange={handleInputChange} name="brigadeId"
              disabled={user?.roles === "USER"} className={inputCls}>
              <option value=""></option>
              {brigade.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Bataillon</label>
            <select value={formFields.bataillonId} onChange={handleInputChange} name="bataillonId" className={inputCls}>
              <option value=""></option>
              {bataillon.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Compagnie</label>
            <select value={formFields.compagnieId} onChange={handleInputChange} name="compagnieId" className={inputCls}>
              <option value=""></option>
              {formFields.bataillonId && compagnie.map((c) => (
                <option key={c.id} value={c.id}>{c.name} - ({c.id})</option>
              ))}
            </select>
          </div>
        </div>

        {/* Ligne 2 : Nom / Ets ou Service / Grade */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Nom et prénoms</label>
            <input type="text" value={formFields.nomprenom} onChange={handleInputChange}
              name="nomprenom" placeholder="Rechercher..." className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Ets ou Service</label>
            <select name="etsouserviceId" value={formFields.etsouserviceId} onChange={handleInputChange} className={inputCls}>
              <option value=""></option>
              {etsouservice.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Grade</label>
            <select name="gradeId" value={formFields.gradeId} onChange={handleInputChange} className={inputCls}>
              <option value=""></option>
              {grades.map((g) => (
                <option key={g.id} value={g.id}>{g.name} - {g.grademer} - {g.gradeair}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Ligne 3 : Opérations / Lieu / Fonction */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Opérationnels / Stages / Détachés</label>
            <select value={formFields.operation} onChange={handleInputChange} name="operation" className={inputCls}>
              <option value="">Sélectionner une option</option>
              <option value="MISSION">MILITAIRE EN OPERATION</option>
              <option value="DETACHE">MILITAIRE DETACHE</option>
              <option value="STAGEINTERNE">STAGE INTERNE</option>
              <option value="STAGEEXTERNE">STAGE EXTERNE</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Lieu (BOA / DSS)</label>
            <select value={formFields.boadssId} onChange={handleInputChange} name="boadssId" className={inputCls}>
              <option value="">Sélectionner BOA ou DSS</option>
              {boaDss.map((b) => <option key={b.id} value={b.id}>{b.bataillonoudss}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Fonction</label>
            <input type="text" value={formFields.fonction} onChange={handleInputChange}
              name="fonction" placeholder="Fonction..." className={inputCls} />
          </div>
        </div>

        {/* Ligne 4 : Spécialité + Regroupement */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Spécialité</label>
            <select onChange={handleInputChange} name="diplomeadministratif" className={inputCls}>
              <option value=""></option>
              <option value="TRESORIER">TRESORIER</option>
              <option value="INFANTERIE">INFANTERIE</option>
              <option value="CHANCELLERIE">CHANCELLERIE</option>
              <option value="TRANSMISSION">TRANSMISSION</option>
              <option value="INFORMATICIEN">INFORMATICIEN</option>
              <option value="MECANICIEN">MECANICIEN</option>
              <option value="ELECTRICIEN">ELECTRICIEN</option>
              <option value="ADMINISTRATION">ADMINISTRATION</option>
            </select>
          </div>


        </div>


        {/* Cadre regroupé : Type Pers + Année + Vague + Checkboxes + Bouton Recherche */}
        <div className="md:col-span-2 bg-gray-800 border border-gray-700 rounded-xl p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <div>
              <label className={labelCls}>Type Personnel</label>
              <select value={formFields.observation} onChange={handleInputChange} name="observation" className={inputCls}>
                <option value=""></option>
                <option value="PDL">PDL</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Année</label>
              <input type="text" value={formFields.annee} onChange={handleInputChange}
                name="annee" placeholder="2024" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Vague</label>
              <input type="text" value={formFields.vague} onChange={handleInputChange}
                name="vague" placeholder="1" className={inputCls} />
            </div>
          </div>

          {/* Checkboxes + Bouton */}
          <div className="flex flex-wrap items-center gap-4 pt-1 border-t border-gray-700 mt-1">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" checked={formFields.exportExcel}
                onChange={e => handleExportExcelChange(e.target.checked)}
                className="w-4 h-4 rounded accent-green-500" />
              <span className="text-sm text-gray-300">Exporter en Excel</span>
            </label>

            <label className={`flex items-center gap-2 select-none ${!formFields.exportExcel ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}>
              <input type="checkbox" checked={formFields.nonVerifier}
                disabled={!formFields.exportExcel}
                onChange={e => handleNonVerifierChange(e.target.checked)}
                className="w-4 h-4 rounded accent-green-500" />
              <span className="text-sm text-gray-300">Personnel non vérifié</span>
            </label>

            <button type="submit"
              className="ml-auto flex items-center gap-2 px-5 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-xl transition-colors duration-200">
              <i className="fa fa-search text-xs"></i>
              Rechercher
            </button>
          </div>
        </div>


        {/* Section PDL — suppression */}
        <div className="flex flex-wrap items-center gap-4 bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 mt-2">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" name="pdl" checked={formFields.pdl}
              onChange={handleInputChange} className="w-4 h-4 rounded accent-red-500" />
            <span className="text-sm font-medium text-gray-300">PDL</span>
          </label>

          <input type="text" name="annee" value={formFields.annee} onChange={handleInputChange}
            placeholder="Année" className={`${inputCls} w-28`} />

          <input type="text" name="vague" value={formFields.vague} onChange={handleInputChange}
            placeholder="Vague" className={`${inputCls} w-24`} />

          <button type="button" onClick={supprimerPDL}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-xl transition-colors duration-200">
            <i className="fa fa-trash text-xs"></i>
            Supprimer PDL
          </button>
        </div>

      </form>
    </div>
  );
};

export default SearchForm;